import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useAppContext } from "../../../app/use-app-context.js";
import { demoEmail } from "./profile-demo.js";
import { ProfileCredentialsTab } from "./ProfileCredentialsTab.js";
import { ProfileDetailsTab } from "./ProfileDetailsTab.js";
import { ProfileOnboardingTab } from "./ProfileOnboardingTab.js";
import { ProfileSkillsTab } from "./ProfileSkillsTab.js";

type ProfileTab = "profile" | "onboarding" | "credentials" | "skills";

const PROFILE_TABS = new Set<ProfileTab>(["profile", "onboarding", "credentials", "skills"]);

function tabFromSearch(value: string | null): ProfileTab {
  return value && PROFILE_TABS.has(value as ProfileTab) ? (value as ProfileTab) : "profile";
}

export function ProfilePage() {
  const { t } = useTranslation();
  const { bootstrap, persona } = useAppContext();
  const [search, setSearch] = useSearchParams();
  const [tab, setTab] = useState<ProfileTab>(() => tabFromSearch(search.get("tab")));

  useEffect(() => {
    setTab(tabFromSearch(search.get("tab")));
  }, [search]);

  if (persona.kind !== "employee") return null;

  const tabs: { value: ProfileTab; label: string }[] = [
    { value: "profile", label: t("profile.tabProfile") },
    { value: "onboarding", label: t("profile.tabOnboarding") },
    { value: "credentials", label: t("profile.tabCredentials") },
    { value: "skills", label: t("profile.tabSkills") },
  ];

  const selectTab = (next: ProfileTab) => {
    setTab(next);
    setSearch(
      (previous) => {
        const nextSearch = new URLSearchParams(previous);
        if (next === "profile") nextSearch.delete("tab");
        else nextSearch.set("tab", next);
        return nextSearch;
      },
      { replace: true },
    );
  };

  return (
    <>
      <h1 className="sr-only">{t("profile.title")}</h1>

      <div className="mb-8 flex items-center gap-5">
        <span
          aria-hidden="true"
          className="grid size-20 shrink-0 place-items-center rounded-full bg-primary text-xl font-medium text-primary-foreground sm:size-24 sm:text-2xl"
        >
          {persona.initials}
        </span>
        <div className="min-w-0">
          <p className="truncate text-2xl font-medium tracking-tight text-foreground sm:text-3xl">
            {persona.name}
          </p>
          <p className="mt-1 truncate text-sm text-muted-foreground sm:text-base">{demoEmail(persona.id)}</p>
        </div>
      </div>

      <div
        className="mb-6 flex gap-6 overflow-x-auto border-b border-border"
        role="tablist"
        aria-label={t("profile.tabsLabel")}
      >
        {tabs.map((option) => {
          const active = tab === option.value;
          return (
            <button
              aria-controls={`profile.tab.${option.value}.panel`}
              aria-selected={active}
              className={`relative inline-flex min-h-11 shrink-0 items-center px-0.5 pb-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/25 ${
                active
                  ? "!font-medium text-primary after:absolute after:inset-x-0 after:bottom-[-1px] after:h-[3px] after:bg-primary"
                  : "!font-medium text-muted-foreground hover:text-foreground"
              }`}
              data-control-id={`profile.tab.${option.value}`}
              id={`profile.tab.${option.value}`}
              key={option.value}
              onClick={() => selectTab(option.value)}
              role="tab"
              type="button"
            >
              {option.label}
            </button>
          );
        })}
      </div>

      <div aria-labelledby={`profile.tab.${tab}`} id={`profile.tab.${tab}.panel`} role="tabpanel">
        {tab === "profile" ? <ProfileDetailsTab persona={persona} /> : null}
        {tab === "onboarding" ? <ProfileOnboardingTab bootstrap={bootstrap} persona={persona} /> : null}
        {tab === "credentials" ? <ProfileCredentialsTab /> : null}
        {tab === "skills" ? <ProfileSkillsTab /> : null}
      </div>
    </>
  );
}
